import { FormControlLabel, Switch, Tooltip, Typography } from "@mui/material";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useRef } from "react";
import { toast } from "react-toastify";
import {
  updateDocumentTemplateEncryptOnRender,
  updateDocumentTemplatePreserveOriginalEncryption,
  uploadDocumentTemplateOriginal,
} from "utils/documentService";

const serverErrorMessage = (error, fallback) =>
  error?.response?.data?.errorMessage || error?.response?.data?.message || fallback;

const preserveTooltip = (template) => {
  if (!template.hasOriginalFile) {
    return "Fill the original PDF in place and keep its own encryption. You will be asked for the original file first.";
  }
  return template.originalEncryption
    ? `Keeps the original's encryption: ${template.originalEncryption}`
    : "The original is not encrypted, so filled copies stay unencrypted";
};

const Toggle = ({ label, tooltip, checked, disabled, onChange }) => (
  <Tooltip title={tooltip} describeChild>
    <FormControlLabel
      sx={{ mr: 0 }}
      onClick={(event) => event.stopPropagation()}
      control={
        <Switch
          size="small"
          checked={checked}
          disabled={disabled}
          onChange={(event) => onChange(event.target.checked)}
        />
      }
      label={<Typography variant="caption">{label}</Typography>}
      labelPlacement="top"
    />
  </Tooltip>
);

/**
 * Encrypt applies our own lock; Preserve keeps the issuer's. Preserve outranks Encrypt on the
 * server, so Encrypt is shown greyed out while Preserve is on rather than pretending to apply.
 */
const TemplateProtectionToggles = ({ template }) => {
  const queryClient = useQueryClient();
  const fileInputRef = useRef(null);
  const refreshTemplates = () => {
    queryClient.invalidateQueries({ queryKey: ["document-templates"] });
    queryClient.invalidateQueries({ queryKey: ["document-template", template.id] });
  };

  const encryptMutation = useMutation({
    mutationFn: updateDocumentTemplateEncryptOnRender,
    onSuccess: refreshTemplates,
    onError: (error) =>
      toast.error(serverErrorMessage(error, "Failed to update encryption setting")),
  });
  const preserveMutation = useMutation({
    mutationFn: updateDocumentTemplatePreserveOriginalEncryption,
    onSuccess: refreshTemplates,
    onError: (error) =>
      toast.error(serverErrorMessage(error, "Failed to update the preserve setting")),
  });
  const uploadOriginalMutation = useMutation({
    mutationFn: uploadDocumentTemplateOriginal,
    onSuccess: () => {
      toast.success("Original PDF stored");
      preserveMutation.mutate({ templateId: template.id, preserveOriginalEncryption: true });
    },
    onError: (error) => toast.error(serverErrorMessage(error, "Failed to upload the original PDF")),
  });

  const preserving = Boolean(template.preserveOriginalEncryption);
  const busy =
    encryptMutation.isLoading || preserveMutation.isLoading || uploadOriginalMutation.isLoading;

  const handlePreserveChange = (checked) => {
    if (checked && !template.hasOriginalFile) {
      fileInputRef.current?.click();
      return;
    }
    preserveMutation.mutate({ templateId: template.id, preserveOriginalEncryption: checked });
  };

  const handleOriginalChosen = (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (file) {
      uploadOriginalMutation.mutate({ templateId: template.id, file });
    }
  };

  return (
    <>
      <Toggle
        label="Encrypt"
        tooltip={
          preserving
            ? "Ignored while Preserve is on: the original's own encryption is kept instead"
            : "Deliver this form as an encrypted, modification-locked PDF. Its password is shown with each document."
        }
        checked={Boolean(template.encryptOnRender)}
        disabled={busy || preserving}
        onChange={(checked) =>
          encryptMutation.mutate({ templateId: template.id, encryptOnRender: checked })
        }
      />
      {template.contentType !== "GENERATED_HTML" && (
        <>
          <Toggle
            label="Preserve"
            tooltip={preserveTooltip(template)}
            checked={preserving}
            disabled={busy}
            onChange={handlePreserveChange}
          />
          <input
            ref={fileInputRef}
            type="file"
            accept="application/pdf"
            hidden
            data-testid="original-pdf-input"
            onClick={(event) => event.stopPropagation()}
            onChange={handleOriginalChosen}
          />
        </>
      )}
    </>
  );
};

export default TemplateProtectionToggles;
